import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import type { Chart } from "chart.js";
import { useWindowSize } from "usehooks-ts";

import { Icon } from "@ui/icons/icon";
import { Loader } from "@ui/loader/loader";
import { useTheme } from "@/components/theme-context/theme-context";
import { ICON_COLLECTION } from "@ui/icons/icon-list";
import { FundDetailsDiagram } from "./fund-details-diagram/fund-details-diagram";
import { fetchFundDetails } from "@/api/fund-details";

const colors = [
  "#5B21B6",
  "#6D28D9",
  "#7C3AED",
  "#8B5CF6",
  "#A78BFA",
  "#C4B5FD",
  "#DDD6FE",
];

interface Props {
  chartRef: React.RefObject<Chart<"pie", any, unknown> | undefined>;
}

export const FundsDetailsInfo = ({ chartRef }: Props) => {
  const { theme } = useTheme();
  const { width } = useWindowSize();

  const isMobile = width < 992;

  const { fund_id } = useParams();

  const {
    data: fund,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["fund-details", fund_id],
    queryFn: fetchFundDetails,
    enabled: !!fund_id,
  });

  // todo: error message?
  if (!fund) {
    return (
      <div
        className="w-full flex flex-col md:flex-row items-center justify-between gap-16px md:gap-24px"
        style={{ height: isMobile ? "1300px" : "446px" }}
      >
        <div className="w-full md:w-1/3 h-full flex flex-col justify-between p-16px rounded-lg bg-white dark:bg-gray-900">
          <Loader />
        </div>
      </div>
    );
  }

  // Calculate token shares from token_factors
  const totalFactors = fund.token_factors.reduce((sum, factor) => sum + factor, 0);
  const tokenShares = fund.token_factors.map((factor) => (factor / totalFactors) * 100);
  const feePercent = fund.fund_factor > 0
    ? (fund.royalty_factor / fund.fund_factor) * 100
    : 0;
  const feeDisplay = feePercent.toLocaleString("en", {
    minimumFractionDigits: feePercent % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });

  // Decode token names: prefer registry names, fall back to hex decoding
  const tokenLabels = fund.tokens.map((hexToken, index) => {
    if (fund.token_names?.[index]) return fund.token_names[index];
    try {
      const assetNameHex = hexToken.substring(56);
      const assetName = Buffer.from(assetNameHex, 'hex').toString('utf8');
      return assetName || `Token ${index + 1}`;
    } catch (e) {
      return `Token ${index + 1}`;
    }
  });

  const chartData = (() => {
    const divideBy = 95 / fund.tokens.length;

    // todo: fix palette? auto-generation?
    const palette =
      fund.tokens.length > 7
        ? fund.tokens.map(
            (_, index) =>
              `rgba(91, 33, 182, ${(
                (100 - (index + 1) * divideBy) /
                100
              ).toFixed(2)})`,
          )
        : colors.slice(0, fund.tokens.length);

    const datasets = [
      {
        data: tokenShares,
        backgroundColor: palette,
        borderWidth: 0,
      },
    ];

    return {
      labels: tokenLabels,
      datasets,
    };
  })();

  const legendData = (() => {
    const divideBy = 95 / fund.tokens.length;
    const palette =
      fund.tokens.length > 7
        ? fund.tokens.map(
            (_, index) =>
              `rgba(91, 33, 182, ${(
                (100 - (index + 1) * divideBy) /
                100
              ).toFixed(2)})`,
          )
        : colors.slice(0, fund.tokens.length);

    return tokenLabels.map((label, index) => ({
      label: label,
      value: Math.round(tokenShares[index]),
      color: palette[index],
    }));
  })();

  // Use fund_name from backend if available, otherwise decode from hex
  const fundName = fund.fund_name || (() => {
    try {
      const nameHex = fund.name.substring(0, 16);
      let str = '';
      for (let i = 0; i < nameHex.length; i += 2) {
        str += String.fromCharCode(parseInt(nameHex.substring(i, i + 2), 16));
      }
      return str;
    } catch (e) {
      return fund.name;
    }
  })();

  return (
    <div className="w-full flex flex-col gap-4">
    <div
      className="w-full flex flex-col md:flex-row items-center justify-between gap-16px md:gap-24px"
      style={{ height: isMobile ? "1300px" : "446px" }}
    >
      {isLoading || isFetching ? (
        <div className="w-full md:w-1/3 h-full flex flex-col justify-between p-16px rounded-lg bg-white dark:bg-gray-900">
          <Loader />
        </div>
      ) : (
        <div className="w-full md:w-1/3 h-full flex flex-col justify-between p-16px rounded-lg bg-white dark:bg-gray-900">
          <div className="w-full flex flex-col gap-12px font-sf-pro">
            <h5 className="text-l font-medium text-gray-800 dark:text-white">
              {fundName}
            </h5>
            <p className="text-m font-medium text-gray-500 leading-normal">
              Fund ID: {fund.name.substring(0, 20)}...
            </p>
          </div>
          <div className="w-full flex flex-col gap-12px">
            <div className="flex items-center font-sf-pro gap-3 cursor-pointer">
              <h6 className="text-l h-full text-gray-500 dark:text-gray-200 font-medium whitespace-nowrap flex items-center">
                Tokens
              </h6>
              <div className="w-full h-full border-b-2 border-dotted border-gray-300 dark:border-gray-700"></div>

              <span className="text-l h-full text-gray-800 dark:text-white font-medium whitespace-nowrap">
                {fund.tokens.length}
              </span>
            </div>

            <div className="flex items-center font-sf-pro gap-3 cursor-pointer">
              <h6 className="text-l h-full text-gray-500 dark:text-gray-200 font-medium whitespace-nowrap flex items-center">
                Fund Factor
              </h6>
              <div className="w-full h-full border-b-2 border-dotted border-gray-300 dark:border-gray-700"></div>

              <span className="text-l h-full text-gray-800 dark:text-white font-medium whitespace-nowrap">
                {fund.fund_factor.toLocaleString("en")}
              </span>
            </div>

            <div className="flex items-center font-sf-pro gap-3 cursor-pointer">
              <h6 className="text-l h-full text-gray-500 dark:text-gray-200 font-medium whitespace-nowrap flex items-center gap-1">
                <Tooltip
                  place="top"
                  id={"fee-help"}
                  style={{
                    backgroundColor: theme === "dark" ? "#fff" : "#111827",
                    color: theme === "dark" ? "#1F2937" : "#fff",
                    borderRadius: "6px",
                  }}
                />
                Fee
                <Icon
                  icon={ICON_COLLECTION.helpCircle}
                  iconSize="12px"
                  iconColor="#5B21B6"
                  tooltip_id="fee-help"
                  tooltip_text="Fee % = (royalty factor / fund factor) x 100"
                />
              </h6>
              <div className="w-full h-full border-b-2 border-dotted border-gray-300 dark:border-gray-700"></div>

              <span className="text-l h-full text-gray-800 dark:text-white font-medium whitespace-nowrap">
                {feeDisplay}%
              </span>
            </div>

            <div className="flex items-center font-sf-pro gap-3 cursor-pointer">
              <h6 className="text-l h-full text-gray-500 dark:text-gray-200 font-medium whitespace-nowrap flex items-center">
                Accrued Royalty (shares)
              </h6>
              <div className="w-full h-full border-b-2 border-dotted border-gray-300 dark:border-gray-700"></div>

              <span className="text-l h-full text-gray-800 dark:text-white font-medium whitespace-nowrap">
                {fund.accrued_royalty.toLocaleString("en")}
              </span>
            </div>

            <div className="flex items-center font-sf-pro gap-3 cursor-pointer">
              <h6 className="text-l h-full text-gray-500 dark:text-gray-200 font-medium whitespace-nowrap flex items-center">
                Transaction
              </h6>
              <div className="w-full h-full border-b-2 border-dotted border-gray-300 dark:border-gray-700"></div>

              <span className="text-l h-full text-gray-800 dark:text-white font-medium whitespace-nowrap text-xs">
                {fund.tx.substring(0, 16)}...
              </span>
            </div>

          </div>
          <div className="w-full flex flex-col gap-12px">
            <div className="flex items-center font-sf-pro gap-3 cursor-pointer">
              <h6 className="text-l h-full text-gray-500 dark:text-white font-medium whitespace-nowrap flex gap-1">
                Creator PKH
              </h6>
              <div className="w-full h-full border-b-2 border-dotted border-gray-300 dark:border-gray-700"></div>
              <span className="text-xs h-full text-gray-800 dark:text-white font-medium whitespace-nowrap">
                {fund.creator_pkh.substring(0, 16)}...
              </span>
            </div>
          </div>
        </div>
      )}
      {isLoading ? (
        <div className="w-full md:w-1/3 h-full flex flex-col gap-32px md:gap-0 justify-between items-center p-16px rounded-lg bg-white dark:bg-gray-900">
          <Loader />
        </div>
      ) : (
        <FundDetailsDiagram
          data={chartData}
          legendData={legendData}
          chartRef={chartRef}
        />
      )}
      {isLoading ? (
        <div className="w-full md:w-1/3 h-full flex flex-col gap-32px md:gap-0 justify-between items-center p-16px rounded-lg bg-white dark:bg-gray-900">
          <Loader />
        </div>
      ) : (
        <div className="w-full md:w-1/3 h-full flex flex-col p-16px rounded-lg bg-white dark:bg-gray-900 overflow-y-auto">
          <h5 className="text-l font-medium text-gray-800 dark:text-white mb-4">
            Tokens
          </h5>
          <div className="w-full overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="font-sf-pro text-xs whitespace-nowrap text-gray-400 text-left font-semibold py-2 px-2">
                    Name
                  </th>
                  <th className="font-sf-pro text-xs whitespace-nowrap text-gray-400 font-semibold py-2 px-2 text-right">
                    <span className="inline-flex items-center gap-1 justify-end w-full">
                      <Tooltip
                        place="top"
                        id={"factor-help"}
                        style={{
                          backgroundColor: theme === "dark" ? "#fff" : "#111827",
                          color: theme === "dark" ? "#1F2937" : "#fff",
                          borderRadius: "6px",
                        }}
                      />
                      Factor
                      <Icon
                        icon={ICON_COLLECTION.helpCircle}
                        iconSize="12px"
                        iconColor="#5B21B6"
                        tooltip_id="factor-help"
                        tooltip_text="Integer basket ratio unit for each token"
                      />
                    </span>
                  </th>
                  <th className="font-sf-pro text-xs whitespace-nowrap text-gray-400 font-semibold py-2 px-2 text-right">
                    Share %
                  </th>
                </tr>
              </thead>
              <tbody>
                {fund.tokens.map((hexToken, index) => {
                  // Decode token name: prefer registry names, fall back to hex
                  let tokenName = `Token ${index + 1}`;
                  if (fund.token_names?.[index]) {
                    tokenName = fund.token_names[index];
                  } else {
                    try {
                      const assetNameHex = hexToken.substring(56);
                      const decoded = Buffer.from(assetNameHex, 'hex').toString('utf8');
                      if (decoded) tokenName = decoded;
                    } catch (e) {}
                  }

                  return (
                    <tr
                      key={hexToken}
                      className="border-b border-gray-100 dark:border-gray-800 hover:bg-violet-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <td className="py-2 px-2">
                        <span className="text-s font-medium text-gray-800 dark:text-white">
                          {tokenName}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right">
                        <span className="text-s font-medium text-gray-800 dark:text-white">
                          {fund.token_factors[index].toLocaleString("en")}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right">
                        <span className="text-s font-medium text-gray-800 dark:text-white">
                          {tokenShares[index].toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};
